import { z } from "zod";
import { Document, DocumentListEntry } from "../document";

export const schema = {
    latest: {
        input: z.void(),
        output: z.array(Document),
    },
    list: {
        input: z.object({
            search: z.coerce.string().min(0).max(100).optional(),
            page: z.coerce.number().int().min(0).optional(),
            pageSize: z.coerce.number().int().min(1).max(30).optional(),
            sortBy: z.enum(["uploaded", "name", "owner", "organizations"]).optional(),
            sort: z.enum(["asc", "desc"]).optional(),
            showOnlyMyFiles: z.boolean().optional(),
            onlyReferenced: z.boolean().optional(),
            organizationId: z.coerce.number().int().optional(),
            authorId: z.coerce.number().int().optional(),
            showPublicFiles: z.boolean().optional().default(true),
            showPrivateFiles: z.boolean().optional().default(true),
            aiStatus: z.enum(["enabled", "disabled", "all"]).optional(),
            _empty: z.boolean().optional(),
        }),
        output: z.object({
            documents: z.array(DocumentListEntry),
            totalFound: z.number(),
            total: z.number(),
        }),
    },
    getDownloadUrl: {
        input: z.object({
            fileId: z.string(),
            type: z.string().nullish(),
        }),
        output: z.object({
            downloadUrl: z.string(),
        }),
    },
    upload: {
        start: {
            input: z.object({
                contentType: z.string().nullish(),
                filename: z.string().max(100).optional().default(""),
                originalFilename: z.string().min(1).max(255),
                description: z.string().max(255).optional().nullable(),
                aiOptOut: z.boolean().optional(),
                context: z
                    .enum(["PUBLIC_ATTACHMENT", "PRIVATE_ATTACHMENT"])
                    .optional()
                    .default("PRIVATE_ATTACHMENT"),
            }),
            output: z.object({
                file: z.lazy(() => Document),
                uploadUrl: z.string(),
            }),
        },
        complete: {
            input: z.object({
                fileId: z.string(),
                // Optional hint for the admin upload notification (PRTL-1322) —
                // tells the server where the upload happened so the alert reads
                // "…in a discussion" / "…to their documents". Omitting it still
                // notifies admins, just with generic wording.
                notifyContext: z
                    .enum(["discussion", "conversation", "social", "documents"])
                    .optional(),
            }),
            output: Document,
        },
    },
    update: {
        start: {
            input: z.object({
                id: z.string(),
                contentType: z.string().nullish(),
                filename: z.string().min(1).max(100),
                description: z.string().max(255).optional().nullable(),
            }),
            output: z.object({
                uploadUrl: z.string(),
            }),
        },
        complete: {
            input: z.object({
                id: z.string(),
            }),
            output: Document,
        },
        metadata: {
            input: z.object({
                id: z.string(),
                filename: z.string().min(1).max(100),
                description: z.string().max(255).optional().nullable(),
                aiOptOut: z.boolean().optional(),
            }),
            output: Document,
        },
    },
    delete: {
        input: z.object({
            fileId: z.string(),
        }),
        output: z.void(),
    },
    changeVisibility: {
        input: z.object({
            fileId: z.string(),
            context: z.enum(["PUBLIC_ATTACHMENT", "PRIVATE_ATTACHMENT"]),
        }),
        output: z.void(),
    },
    triggerAI: {
        input: z.object({
            fileId: z.string(),
        }),
        output: z.void(),
    },
    clearAI: {
        input: z.object({
            fileId: z.string(),
        }),
        output: z.void(),
    },
    /** PRTL-1497: an owned file's AI transcript state, polled while it is being made. */
    aiStatus: {
        input: z.object({
            fileId: z.string(),
        }),
        output: Document.pick({
            aiOptOut: true,
            aiDescriptionStatus: true,
            aiDescriptionShort: true,
            aiDescriptionLong: true,
        }),
    },
};
